/**
 * Страница логина — с поддержкой OAuth (VK, Telegram).
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button, Card, Form, Input, Typography, message, Divider, Space, Spin } from 'antd';
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import { useNavigate, Link, Navigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import api from '../services/api';
import '../authPages.css';

const { Title, Text } = Typography;

// Типы OAuth конфигурации
interface OAuthConfig {
  vk: { enabled: boolean; app_id: string };
  telegram: { enabled: boolean; bot_username: string };
}

// Расширяем window для Telegram callback
declare global {
  interface Window {
    onTelegramAuth: (user: any) => void;
  }
}

const LoginPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [oauthConfig, setOauthConfig] = useState<OAuthConfig | null>(null);
  const [tgLoading, setTgLoading] = useState(false);
  const navigate = useNavigate();
  const login = useAuthStore((s) => s.login);
  const oauthLogin = useAuthStore((s) => s.oauthLogin);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const tgContainerRef = useRef<HTMLDivElement>(null);

  // Обработка успешного OAuth ответа на странице логина
  const handleOAuthSuccess = useCallback(async (data: any) => {
    await oauthLogin(data);
    const tenant = useAuthStore.getState().tenant;
    message.success(tenant ? `Добро пожаловать в ${tenant.name}!` : 'Добро пожаловать!');
    navigate('/');
  }, [oauthLogin, navigate]);

  // Загрузка OAuth конфигурации при монтировании
  useEffect(() => {
    const fetchOAuthConfig = async () => {
      try {
        const { data } = await api.get('/auth/admin/oauth-config');
        setOauthConfig(data);
      } catch {
        // OAuth недоступен — показываем только email-форму
        console.warn('OAuth config unavailable');
      }
    };
    fetchOAuthConfig();
  }, []);

  // Обработка VK ID callback — если в URL есть ?code= + state + device_id
  // (больше не нужно — callback обрабатывается на основном домене zhorka.tech)

  // Инициализация Telegram Login Widget
  useEffect(() => {
    if (!oauthConfig?.telegram?.enabled || !tgContainerRef.current) return;

    // Глобальный callback для Telegram Widget
    window.onTelegramAuth = async (user: any) => {
      setTgLoading(true);
      try {
        const { data } = await api.post('/auth/admin/telegram-auth', {
          id: user.id,
          first_name: user.first_name,
          last_name: user.last_name,
          username: user.username,
          photo_url: user.photo_url,
          auth_date: user.auth_date,
          hash: user.hash,
        });
        await handleOAuthSuccess(data);
      } catch (error: any) {
        const detail = error?.response?.data?.detail;
        const errorMsg = typeof detail === 'string' ? detail : 'Ошибка авторизации через Telegram';
        message.error(errorMsg);
      } finally {
        setTgLoading(false);
      }
    };

    // Вставляем Telegram Widget script
    const container = tgContainerRef.current;
    container.innerHTML = '';
    const script = document.createElement('script');
    script.src = 'https://telegram.org/js/telegram-widget.js?22';
    script.setAttribute('data-telegram-login', oauthConfig.telegram.bot_username);
    script.setAttribute('data-size', 'large');
    script.setAttribute('data-onauth', 'onTelegramAuth(user)');
    script.setAttribute('data-request-access', 'write');
    script.async = true;
    container.appendChild(script);

    return () => {
      container.innerHTML = '';
    };
  }, [oauthConfig, handleOAuthSuccess]);

  // Если уже авторизован — сразу в дашборд
  if (isAuthenticated) return <Navigate to="/" replace />;

  const onFinish = async (values: { email: string; password: string }) => {
    setLoading(true);
    try {
      await login(values.email, values.password);
      const tenant = useAuthStore.getState().tenant;
      message.success(tenant ? `Добро пожаловать в ${tenant.name}!` : 'Добро пожаловать!');
      navigate('/');
    } catch {
      message.error('Неверный логин или пароль');
    } finally {
      setLoading(false);
    }
  };

  // Редирект на VK ID через основной домен (PKCE обрабатывается сервером)
  const handleVkLogin = () => {
    if (!oauthConfig?.vk?.enabled) return;

    const returnUrl = `${window.location.origin}/admin/login`;
    // Основной домен — извлекаем из текущего хоста (admin.zhorka.tech → zhorka.tech)
    const hostParts = window.location.hostname.split('.');
    const mainDomain = hostParts.length > 2
      ? `https://${hostParts.slice(1).join('.')}`
      : window.location.origin;

    window.location.href = `${mainDomain}/auth/vk/start?return_url=${encodeURIComponent(returnUrl)}&source=admin`;
  };

  return (
    <div className="zhorka-auth-page">
      <Card className="zhorka-auth-card">
        <div className="zhorka-auth-brand">
          <div className="zhorka-auth-brandline"><img src={`${import.meta.env.BASE_URL}favicon.svg`} alt="" /><strong>Жорка</strong></div>
          <span className="zhorka-auth-eyebrow">Кабинет компании</span>
          <Title level={3}>Добро пожаловать</Title>
          <Text type="secondary">Управляйте заказами, меню и маркетингом в одном месте.</Text>
        </div>
        <Form onFinish={onFinish} layout="vertical">
          <Form.Item name="email" rules={[{ required: true, message: 'Введите email' }]}>
            <Input prefix={<UserOutlined />} placeholder="Email" size="large" />
          </Form.Item>
          <Form.Item name="password" rules={[{ required: true, message: 'Введите пароль' }]}>
            <Input.Password prefix={<LockOutlined />} placeholder="Пароль" size="large" />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" size="large" block loading={loading}>
              Войти
            </Button>
          </Form.Item>
        </Form>

        {/* OAuth кнопки */}
        {oauthConfig && (oauthConfig.vk?.enabled || oauthConfig.telegram?.enabled) && (
          <>
            <Divider style={{ margin: '4px 0 16px' }}>или</Divider>
            <Space direction="vertical" style={{ width: '100%' }} size={12}>
              {/* Кнопка VK */}
              {oauthConfig.vk?.enabled && (
                <Button
                  block
                  size="large"
                  onClick={handleVkLogin}
                  style={{
                    background: '#4C75A3',
                    borderColor: '#4C75A3',
                    color: '#fff',
                    height: 40,
                    fontWeight: 500,
                  }}
                >
                  <span style={{ marginRight: 8 }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="white" style={{ verticalAlign: 'middle' }}>
                      <path d="M12.785 16.241s.288-.032.436-.194c.136-.148.132-.427.132-.427s-.02-1.304.587-1.496c.598-.189 1.367 1.258 2.182 1.814.617.42 1.085.328 1.085.328l2.175-.03s1.138-.07.599-.964c-.044-.073-.314-.661-1.618-1.869-1.366-1.265-1.183-1.06.462-3.248.998-1.33 1.398-2.143 1.273-2.49-.12-.331-.856-.244-.856-.244l-2.451.015s-.182-.025-.317.056c-.131.079-.216.263-.216.263s-.387 1.028-.903 1.903c-1.088 1.85-1.524 1.948-1.702 1.833-.413-.267-.31-1.075-.31-1.649 0-1.794.272-2.541-.53-2.735-.266-.064-.462-.107-1.144-.114-.874-.009-1.614.003-2.033.208-.28.137-.495.44-.364.458.162.022.53.099.725.363.252.341.243 1.106.243 1.106s.145 2.11-.338 2.372c-.332.18-.787-.187-1.762-1.868-.5-.86-.877-1.813-.877-1.813s-.073-.178-.203-.274c-.157-.116-.377-.153-.377-.153l-2.328.015s-.35.01-.478.161c-.114.134-.009.412-.009.412s1.82 4.258 3.882 6.403c1.889 1.967 4.034 1.838 4.034 1.838h.973z"/>
                    </svg>
                  </span>
                  Войти через VK
                </Button>
              )}

              {/* Кнопка/виджет Telegram */}
              {oauthConfig.telegram?.enabled && (
                <div style={{ position: 'relative' }}>
                  {tgLoading && (
                    <div style={{ textAlign: 'center', padding: 8 }}>
                      <Spin size="small" />
                      <Text type="secondary" style={{ marginLeft: 8 }}>Авторизация через Telegram...</Text>
                    </div>
                  )}
                  <div ref={tgContainerRef} style={{ display: 'flex', justifyContent: 'center' }} />
                </div>
              )}
            </Space>
          </>
        )}

        <div style={{ textAlign: 'center', marginTop: 16 }}>
          <Text type="secondary">Нет аккаунта? </Text>
          <Link to="/register">Зарегистрироваться</Link>
        </div>
      </Card>
    </div>
  );
};

export default LoginPage;
